import React, { useEffect, useState } from 'react';
import trackEvent from '../../utils/event-helper';
import ItemsSlider from './ItemsSlider';
import FiltersDropdown from './FiltersDropdown';
import FiltersDialog from './FiltersDialog';
import { setFilters, useStateValue } from '../../state/stateProvider';
import useFilters from '../../hooks/gallery/useFilters';

import styles from './filters.module.scss';

const Filters = ({
  adjustFiltersClass, content, localStoragePointer, isMobile, queryParamFilter,
}) => {
  const {
    filtersBar,
    filtersBlock,
    filterInteractiveElement,
    activeFilter,
    filterMenuListItem,
    filterMenuDoneIcon,
    disabled,
    separator,
    filtersBarContainer,
    dialogOpen,
  } = styles;

  const dirtyOption = 'None (No filter)';

  const { getFilters } = useFilters(content.filters, localStoragePointer);
  const [{ filters }, dispatch] = useStateValue();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  /**
     * Initiating filters preset once the component is loaded
     */
  const initFiltersPreset = () => {
    const allFilters = {
      ...getFilters(),
      ...(queryParamFilter && { filter: queryParamFilter }),
    };

    setFilters(dispatch, allFilters);
  };

  /**
     * Updating filters state and saving preset in local storage
     * each time filters change
     * @param selectedFilters - Filters object
     */
  const setSelectedFilters = (selectedFilters) => {
    setFilters(dispatch, selectedFilters);
    if (localStoragePointer) {
      localStorage.setItem(localStoragePointer, JSON.stringify(selectedFilters));
    }
  };

  /**
     * Tracking selected filter values from dropdown or mobile dialog
     * @param value
     */
  const trackFilterValueSelected = (value) => {
    trackEvent('filter_value_selected', {
      action: 'Selection',
      element: 'selection_field',
      type: 'element_value_selected',
      tabName: value,
    });
  };

  /**
     * Tracking needs selection events
     * @param value - needs selection
     */
  const trackNeedsValueSelected = (value) => {
    trackEvent('need_clicked', {
      action: 'click',
      element: 'button',
      tabName: value,
    });
  };

  /**
     * Simulation of filters state management.
     * @param type - filter group type
     * @param value - selected/unselected value
     */
  const onFilterItemMarked = (type: string, value: string | string[]) => {
    const clonedFilters = { ...filters };
    if (type === 'needs') {
      const group = clonedFilters[type];
      if (group.includes(value)) {
        if (group.length === 1) {
          return;
        }
        clonedFilters[type] = group.filter((i) => i !== value);
      } else {
        group.push(value);
        clonedFilters[type] = group;
        trackNeedsValueSelected(value);
      }
    } else {
      clonedFilters[type] = value;
      trackFilterValueSelected(value);
    }
    setSelectedFilters(clonedFilters);
  };

  /**
     * Function called from filters mobile dialog when user presses SHOW RESULTS button
     * @param dialogFilters - object containing filters setup from mobile dialog
     */
  const onFilterTriggeredFromDialog = (dialogFilters) => {
    const clonedFilters = { ...filters };
    clonedFilters.timeline = dialogFilters.timeline;
    clonedFilters.filter = dialogFilters.filter;
    setSelectedFilters(clonedFilters);
    trackFilterValueSelected(dialogFilters.timeline);
    trackFilterValueSelected(dialogFilters.filter);
  };

  /**
     * Generating Needs section
     */
  const generateNeedsOptions = () => content.filters.needs.map((n) => (
        // @ts-ignore
        <button itemId={n}
                key={n}
                className={`${filters.needs?.includes(n) ? activeFilter : ''} ${filterInteractiveElement}`}
                onClick={() => onFilterItemMarked('needs', n)}>{n}</button>
  ));

  const generateDropDownList = (options: string[], type: string) => options.map((op) => (
        <li key={op} className={
            `${filterMenuListItem} 
          ${op === dirtyOption && !filters[type] ? disabled : ''}`}
            onClick={() => onFilterItemMarked(type, op !== dirtyOption ? op : null)}>
            {filters[type]?.toLowerCase() === op?.toLowerCase() && (
              <i className={`ic icon-check-mark ${filterMenuDoneIcon}`}/>
            )}
            <span>{op}</span>
        </li>
  ));

  /**
     * Generating Timeline filter options
     */
  const generateTimelineOptions = () => {
    const listData = [dirtyOption, content.filters.timeline].flat();
    return generateDropDownList(listData, 'timeline');
  };

  /**
     * Generating More Filters options
     */
  const generateMoreFiltersOptions = () => {
    const listData = [dirtyOption, content.filters.moreFilters].flat();
    return generateDropDownList(listData, 'filter');
  };

  useEffect(() => {
    initFiltersPreset();
  }, [content.vertical]);

  return (
    <section className={
      `${filtersBarContainer} ${adjustFiltersClass} responsiveGridLayout ${isDialogOpen ? dialogOpen : ''}`
    }>
      <div className={`${filtersBar} responsiveMainContent`}>
        <div className={filtersBlock}>
          {isMobile &&
            <FiltersDialog
              timeline={content.filters.timeline}
              filters={content.filters.moreFilters}
              onToggleDialog={(open) => setIsDialogOpen(open)}
              showDialog={isDialogOpen}
              selectedFiltersObject={filters}
              filterResults={onFilterTriggeredFromDialog}/>
          }
          <ItemsSlider>
            {generateNeedsOptions()}
          </ItemsSlider>
          {!isMobile && (
            <>
              <div className={separator}/>
              {content.filters.timeline ?
                <FiltersDropdown label='Timeline' selectedItem={filters.timeline}>
                  {generateTimelineOptions()}
                </FiltersDropdown> : null
              }
              <FiltersDropdown label='Filters' selectedItem={filters.filter} icon='icon-union'>
                {generateMoreFiltersOptions()}
              </FiltersDropdown>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Filters;
